import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { hasPI } from '../../../lib/permissions';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

const CATEGORIES = [
  { id: 'financial',   label: 'Financial Readiness',  weight: 25 },
  { id: 'operational', label: 'Operational Story',     weight: 20 },
  { id: 'management',  label: 'Management Readiness',  weight: 20 },
  { id: 'strategic',   label: 'Strategic Positioning', weight: 20 },
  { id: 'data',        label: 'Data & Reporting',      weight: 15 },
];

const STATUS_CYCLE = ['green', 'amber', 'red'];
const STATUS_LABELS = { green: 'On Track', amber: 'Caution', red: 'At Risk' };

function GaugeRing({ score }) {
  const color = score >= 70 ? '#16a34a' : score >= 40 ? '#d97706' : '#dc2626';
  const label = score >= 70 ? 'Exit Ready' : score >= 40 ? 'Getting There' : 'Needs Work';
  const r = 52;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <svg width={130} height={130} viewBox="0 0 130 130" aria-label={`Exit readiness score: ${score}/100`}>
        <circle cx={65} cy={65} r={r} fill="none" stroke="#f1f5f9" strokeWidth={10} />
        <circle cx={65} cy={65} r={r} fill="none" stroke={color} strokeWidth={10}
          strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 65 65)" />
        <text x={65} y={60} textAnchor="middle" fontSize={26} fontWeight="800" fill={color}>{score}</text>
        <text x={65} y={78} textAnchor="middle" fontSize={11} fill="#94a3b8">/100</text>
      </svg>
      <div style={{ fontSize: 13, fontWeight: 600, color }}>Exit Readiness Score</div>
      <div style={{ fontSize: 12, color: '#94a3b8' }}>{label}</div>
    </div>
  );
}

export default function PIExitReadinessTab({ portco, currentUser }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiChecklist, setAiChecklist] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ category: 'financial', criterion: '', notes: '' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ category: '', criterion: '', status: 'amber', notes: '' });
  const canEdit = hasPI(currentUser, 'pi.exit.edit');

  const load = useCallback(() => {
    piApi.getExitReadinessItems(portco.id).then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [portco.id]);
  useEffect(() => { load(); }, [load]);

  const computeScore = (categoryItems) => {
    if (!categoryItems.length) return 0;
    return Math.round((categoryItems.filter(i => i.status === 'green').length / categoryItems.length) * 100);
  };

  const compositeScore = Math.round(CATEGORIES.reduce((total, cat) => {
    const catItems = items.filter(i => i.category === cat.id);
    return total + (computeScore(catItems) * cat.weight / 100);
  }, 0));

  const amberRedCount = items.filter(i => i.status !== 'green').length;
  const projectedMonths = amberRedCount * 3;
  const projectedDate = new Date();
  projectedDate.setMonth(projectedDate.getMonth() + projectedMonths);
  const projectedDateStr = projectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleToggleStatus = async (item) => {
    if (!canEdit) return;
    const currentIdx = STATUS_CYCLE.indexOf(item.status);
    const nextStatus = STATUS_CYCLE[(currentIdx + 1) % STATUS_CYCLE.length];
    await piApi.updateExitItem(portco.id, item.id, { status: nextStatus }).catch(() => {});
    load();
    piApi.updatePortco(portco.id, { exit_readiness_score: compositeScore }).catch(() => {});
  };

  const handleAddItem = async () => {
    if (!newItem.criterion) return;
    await piApi.createExitItem(portco.id, { ...newItem, status: 'amber' }).catch(() => {});
    setNewItem({ category: 'financial', criterion: '', notes: '' });
    setShowAddForm(false);
    load();
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({
      category: item.category || 'financial',
      criterion: item.criterion || '',
      status: item.status || 'amber',
      notes: item.notes || '',
    });
  };

  const handleUpdate = async () => {
    if (!editForm.criterion) return;
    await piApi.updateExitItem(portco.id, editId, editForm).catch(() => {});
    setEditId(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this exit criterion?')) return;
    await piApi.deleteExitItem(portco.id, id).catch(() => {});
    load();
  };

  const handleGenerateChecklist = async () => {
    setAiLoading(true);
    try {
      const res = await piApi.generateExitChecklist(portco.id);
      setAiChecklist(res.data.checklist);
    } catch (e) { console.error('[Exit] AI error:', e); }
    finally { setAiLoading(false); }
  };

  const inputStyle = { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, width: '100%', boxSizing: 'border-box' };
  const cellStyle = { padding: '10px 14px' };
  const colCount = canEdit ? 5 : 4;

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
        <div className="pi-card" style={{ padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 200 }}>
          <GaugeRing score={compositeScore} />
        </div>

        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {CATEGORIES.map(cat => {
            const catItems = items.filter(i => i.category === cat.id);
            const catScore = computeScore(catItems);
            const color = catScore >= 70 ? '#16a34a' : catScore >= 40 ? '#d97706' : '#dc2626';
            return (
              <div key={cat.id} className="pi-card" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: '#334155', lineHeight: 1.3 }}>{cat.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color }}>{catScore}</span>
                </div>
                <div style={{ height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{ width: `${catScore}%`, height: '100%', borderRadius: 2, background: color }} />
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{catItems.length} items · {cat.weight}% weight</div>
              </div>
            );
          })}
        </div>
      </div>

      {amberRedCount > 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: '#92400e', marginBottom: 20 }}>
          At current trajectory, this portco is projected to reach exit-ready (score &ge; 70) by approximately <strong>{projectedDateStr}</strong> ({amberRedCount} amber/red items &times; 3 months each).
        </div>
      )}

      <div className="pi-card" style={{ marginBottom: 16 }}>
        <div className="pi-card-header">
          <span className="pi-card-title">Exit Criteria</span>
          {canEdit && (
            <button className="pi-btn-ghost" style={{ fontSize: 11 }} onClick={() => { setShowAddForm(s => !s); setEditId(null); }} aria-label="Add criterion">
              <Plus size={12} /> Add Criterion
            </button>
          )}
        </div>
        {showAddForm && canEdit && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 10, alignItems: 'center' }}>
            <select value={newItem.category} onChange={e => setNewItem(n => ({ ...n, category: e.target.value }))}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5 }}>
              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <input value={newItem.criterion} onChange={e => setNewItem(n => ({ ...n, criterion: e.target.value }))}
              placeholder="Criterion description" style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            <input value={newItem.notes} onChange={e => setNewItem(n => ({ ...n, notes: e.target.value }))}
              placeholder="Notes (optional)" style={{ flex: 0.5, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            <button className="pi-btn-primary" style={{ fontSize: 12 }} onClick={handleAddItem}>Add</button>
            <button className="pi-btn-ghost" style={{ fontSize: 12 }} onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#fafbff', borderBottom: '1px solid #f1f5f9' }}>
                {['Category', 'Criterion', 'Status', 'Notes', ...(canEdit ? [''] : [])].map(h => (
                  <th key={h} style={{ padding: '8px 14px', textAlign: 'left', color: '#64748b', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={colCount}><div className="pi-loading" style={{ padding: 16 }}><div className="pi-spinner" /></div></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={colCount}>
                  <div className="pi-empty" style={{ padding: 24 }}>
                    <div className="pi-empty-title">Configure your exit readiness criteria to start scoring.</div>
                    {canEdit && <button className="pi-btn-ghost" onClick={() => setShowAddForm(true)}><Plus size={13} /> Add first criterion</button>}
                  </div>
                </td></tr>
              ) : items.map(item => {
                if (editId === item.id && canEdit) {
                  return (
                    <tr key={item.id} style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <td style={cellStyle}>
                        <select value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
                          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                        </select>
                      </td>
                      <td style={cellStyle}>
                        <input value={editForm.criterion} onChange={e => setEditForm(f => ({ ...f, criterion: e.target.value }))} style={inputStyle} />
                      </td>
                      <td style={cellStyle}>
                        <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                          <option value="green">On Track</option>
                          <option value="amber">Caution</option>
                          <option value="red">At Risk</option>
                        </select>
                      </td>
                      <td style={cellStyle}>
                        <input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" style={inputStyle} />
                      </td>
                      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button type="button" className="pi-btn-primary" style={{ padding: '4px 10px', fontSize: 11 }} onClick={handleUpdate}>
                            <Save size={11} /> Save
                          </button>
                          <button type="button" className="pi-btn-ghost" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => setEditId(null)}>
                            <X size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={cellStyle}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: '#f1f5f9', color: '#475569' }}>
                        {CATEGORIES.find(c => c.id === item.category)?.label || item.category}
                      </span>
                    </td>
                    <td style={{ ...cellStyle, color: '#334155', fontWeight: 500 }}>{item.criterion}</td>
                    <td style={cellStyle}>
                      <button
                        type="button"
                        className={`pi-badge pi-badge-${item.status === 'green' ? 'green' : item.status === 'amber' ? 'amber' : 'red'}`}
                        style={{ cursor: canEdit ? 'pointer' : 'default', border: 'none', background: 'inherit' }}
                        onClick={() => handleToggleStatus(item)}
                        aria-label={`Status: ${STATUS_LABELS[item.status]}${canEdit ? '. Click to change.' : ''}`}
                        title={canEdit ? 'Click to cycle status' : ''}
                      >
                        {STATUS_LABELS[item.status]}
                      </button>
                    </td>
                    <td style={{ ...cellStyle, color: '#64748b', fontSize: 12 }}>{item.notes || '—'}</td>
                    {canEdit && (
                      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button type="button" className="pi-btn-ghost" style={{ padding: '4px 6px', fontSize: 11 }} onClick={() => { startEdit(item); setShowAddForm(false); }} title="Edit">
                            <Pencil size={13} />
                          </button>
                          <button type="button" className="pi-btn-ghost" style={{ padding: '4px 6px', fontSize: 11, color: '#dc2626' }} onClick={() => handleDelete(item.id)} title="Delete">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {(hasPI(currentUser, 'pi.exit.edit')) && (
        <div className="pi-card">
          <div className="pi-card-header">
            <span className="pi-card-title">Exit Prep Checklist</span>
            <button className="pi-ai-btn" onClick={handleGenerateChecklist} disabled={aiLoading} aria-label="Generate exit prep checklist">
              <Sparkles size={13} />
              {aiLoading ? 'Building your exit checklist...' : 'Generate Exit Prep Checklist'}
            </button>
          </div>
          {aiLoading && <div className="pi-loading" style={{ padding: '14px 18px' }}><div className="pi-spinner" /><span>Building your exit checklist...</span></div>}
          {aiChecklist ? (
            <div className="pi-card-body">
              <pre style={{ fontSize: 13, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{aiChecklist}</pre>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="pi-btn-ghost" style={{ fontSize: 12 }} onClick={() => navigator.clipboard?.writeText(aiChecklist)}>Copy</button>
                <button className="pi-btn-ghost" style={{ fontSize: 12 }} onClick={() => { setAiChecklist(null); handleGenerateChecklist(); }}>Regenerate</button>
              </div>
            </div>
          ) : !aiLoading && (
            <div className="pi-card-body" style={{ color: '#94a3b8', fontSize: 13 }}>
              Generate a prioritised action checklist based on your current exit readiness criteria.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
